import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/rootReducer";
import Navbar from ".././shared/Navbar";
import { Grid, Button, Theme, makeStyles } from "@material-ui/core";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import api from "../../store/api";
import { setDriverLoginToken } from "../../store/modules/authSlice";
import ErrorMessage from "../shared/messages/ErrorMessage";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { IAppPropsWithRoute } from "../../common/types";
import DropdownSelectOption, { SelectItemType } from "./DropdownSelectOption";
import useWindowSize from "../shared/useWindowSize";
import { pcrstate } from "../../store/modules/PcrSlice";

interface ChooseVehicleProps extends IAppPropsWithRoute {}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    background: "blue",
    marginBottom: "10px",
  },
  root2: {
    background: "grey",
    marginBottom: "10px",
  },
  button: {
    backgroundColor: "#F4B827",
    color: "black",
    fontWeight: 600,
  },
}));

const SHIFT_TYPES = [
  {
    value: 1,
    text: "Patient Transport",
  },
  {
    value: 2,
    text: "Event Medical",
  },
  {
    value: 3,
    text: "Medical Response",
  },
  {
    value: 4,
    text: "Health Share",
  },
];
const NON_SMA_SHIFT_TYPES = [
  {
    value: 1,
    text: "Patient Transport",
  },
];

const NO_CLUSTER_SELECTED = "NO_CLUSTER_SELECTED";

function ChooseVehicleWidget(props: ChooseVehicleProps): JSX.Element {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const classes = useStyles();
  const { appState, authStore } = useSelector((state: RootState) => state);
  const [isSMAUser, setIsSMAUser] = useState((authStore.data?.IsSMAUser != null) ? authStore.data?.IsSMAUser : true);
  const [errorMessage, setErrorMessage] = useState("");
  const [vehicleNo, setVehicleNo] = useState<string | number>("");
  const [shiftType, setShiftType] = useState<string | number>(isSMAUser ? SHIFT_TYPES[0].value : NON_SMA_SHIFT_TYPES[0].value);
  const [clusterId, setClusterId] = useState<string | number>("");
  const [isVehicleNoError, setIsVehicleNoError] = useState(false);

  const [vehicleNoList, setVehicleNoList] = useState<SelectItemType[]>([]);
  const [clustersList, setClustersList] = useState<SelectItemType[]>([]);
  const wrapperAppid = useSelector(pcrstate);

  useEffect(() => {
    setIsSMAUser((authStore.data?.IsSMAUser != null) ? authStore.data?.IsSMAUser : true);
    loadVehicles();
    checkVehicleSession();
  }, []);

  const { windowWidth } = useWindowSize();
  const isItBigScreen = windowWidth > 1200;

  useEffect(() => {
    if (shiftType === 3) {
      setClusterId(NO_CLUSTER_SELECTED);
    }
    if (shiftType === 3 && clustersList.length === 0) {
      loadCustersList();
    }
  }, [shiftType]);

  const loadCustersList = async (): Promise<void> => {
    try {
      const res = await api.getLocationClusters("");
      if (!res.errorRaised && res.model?.length > 0) {
        setClustersList([
          {
            value: NO_CLUSTER_SELECTED,
            text: "Choose",
          },
          ...res.model,
        ]);
        setClusterId(NO_CLUSTER_SELECTED);
      } else {
        throw "Error loading clusters";
      }
    } catch (error) {
      setErrorMessage("Error while getting clusters list, try to refresh the page");
    }
  };

  const loadVehicles = async () => {
    let vehicleNumbers: String[] = [];
    if(isSMAUser)
      await api
        .getVehicles("", { fullNumber: true })
        .then((res) => {
          vehicleNumbers = res.model.map((v: any) => {
            return String(v.text);
          });
        })
        .catch((error) => {
          setErrorMessage("Error while getting vehicles list, try to refresh the page");
        });
    else if (authStore.data?.IsCustomer)
    await api
    .getVehiclesBasedOnCustomer("", { locationId: authStore.data?.location||null })
    .then((res) => {
      vehicleNumbers = res.model.map((v: any) => {
        return String(v.text);
      });
    })
    .catch((error) => {
      setErrorMessage("Error while getting vehicles list, try to refresh the page");
    });
    else
      await api
        .getVehiclesBasedOnCustomer("", { locationId: authStore.data?.location })
        .then((res) => {
          vehicleNumbers = res.model.map((v: any) => {
            return String(v.text);
          });
        })
        .catch((error) => {
          setErrorMessage("Error while getting vehicles list, try to refresh the page");
        });

    //add empty vehicle
    const vehicleSelectOption = [
      {
        value: "",
        text: "No Vehicle",
      },
    ];
    vehicleSelectOption.push(
      ...vehicleNumbers.map((v) => {
        return {
          value: `${v}`,
          text: `${v}`,
        };
      })
    );
    setVehicleNoList(vehicleSelectOption);
  };

  const checkVehicleSession = async (): Promise<void> => {
    await api
      .getUserVehicleSession("")
      .then((_res) => {
        if (!_res.errorRaised && _res.model?.vehicle !== null) {
          setVehicleNo(_res.model.vehicle);
        }
      })
      .catch();
  };

  const onContinue = async (): Promise<void> => {
    if (vehicleNo !== "") {
      //localStorage.getItem("mdtwrapper_app_id");
      const appId = wrapperAppid.appId;
      const isApp = wrapperAppid.isApp;
      if (isApp === true) {
        const data = {
          AppId: appId == null ? "" : appId,
          Type: "Get from Local storage",
        };

        var res = await api.AddErrortoErrorLog('', data);
      }

      let queryStr = `vehicle=${vehicleNo}&shiftType=${shiftType}`;
      if (shiftType === 3 && clusterId !== NO_CLUSTER_SELECTED) {
        queryStr += `${clusterId ? `&clusterId=${clusterId}` : ""}`;
      }
      queryStr += `${appId ? `&appId=${appId}` : ""}`;
      try {
        const _res = await api.assignVehicleShiftClusterToDriver(queryStr);
        if (!_res.errorRaised) {
          dispatch(setDriverLoginToken(_res.token));         
          if (shiftType === 1) {
            //props.history.push("/transferSchedule"); // default page for Patient transport shift
            localStorage.setItem("TeammemberContinue", "No")
            props.history.push("/teamMembers");
          } else if (shiftType === 2) {
            props.history.push("/eventMedical/route"); // default page for Event Medical shift
          } else if (shiftType === 3) {
            props.history.push("/agecarecalloutfordriver"); // default page for Aged Care/Medical Response shift
          } else if (shiftType === 4) {
            props.history.push("/healthShare/route"); // default page for Health Share shift
          }

          props.history.push("/teamMembers");
        }
      } catch {
        setErrorMessage("Error saving vehicle selection, please try agian");
      }
    } else {
      setIsVehicleNoError(true);
    }
  };

  return (
    <>
      <Navbar humberMenu={null} righButtonName={authStore.userName ?? ""} />
      <div style={{ width: "100%", height: "100%", backgroundColor: "#00152E" }}>
        <Grid container style={{ backgroundColor: "#00152E", paddingLeft: 90, paddingTop: isItBigScreen ? 90 : 20 }}>
          <Grid xs={12} item>
            <div style={{ fontSize: isItBigScreen ? 56 : 32, color: "white" }}>Hi {authStore.data?.givenName},</div>
          </Grid>
          <DropdownSelectOption
            title={"Which vehicle are you in today?"}
            selectedValue={vehicleNo}
            isError={isVehicleNoError}
            errorText={"Vehicle number is required"}
            itemsList={vehicleNoList}
            onChange={(value) => {
              setVehicleNo(value);
              setIsVehicleNoError(false);
            }}
          />
          {isSMAUser && (
            <DropdownSelectOption
              title={"Which type of shift are you working?"}
              selectedValue={shiftType}
              itemsList={isSMAUser ? SHIFT_TYPES : NON_SMA_SHIFT_TYPES}
              onChange={(s) => {
                setErrorMessage("");
                setShiftType(s);
              }}
            />
          )}
          {/* show only for Aged Care/Medical Response Shift */}
          {shiftType === 3 && clustersList.length > 0 && (
            <DropdownSelectOption
              title={"Which cluster are you working?"}
              selectedValue={clusterId}
              itemsList={clustersList}
              onChange={(s) => setClusterId(s)}
            />
          )}

          {errorMessage && (
            <Grid style={{ paddingTop: isItBigScreen ? 30 : 10 }}>
              <ErrorMessage errorMessage={errorMessage} />
            </Grid>
          )}
          <Grid xs={12} item style={{ paddingTop: isItBigScreen ? 80 : 28, paddingBottom: 80 }}>
            <Button variant="contained" onClick={onContinue} className={classes.button} endIcon={<ArrowForwardIosIcon />}>
              Continue
            </Button>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

export default React.memo(ChooseVehicleWidget);
